import * as R from 'ramda'

import {
  TemperatureTypes,
  UnitTypes,
  WeightTypes,
} from '../constants/localization/units'
import { Field, Nil, Primitive, UnitsState } from '../types'
import { round } from './index'

export function kgToLbs(x: number | Nil) {
  return R.isNil(x) ? x : round(x * 2.20462262, 4)
}

export function lbsToKg(x: number | Nil) {
  return R.isNil(x) ? x : round(x * 0.45359237, 4)
}

export function celsiusToFahrenheit(temperature: number | Nil) {
  return R.isNil(temperature) ? temperature : (temperature * 9) / 5 + 32
}

export function fahrenheitToCelsius(temperature: number | Nil) {
  return R.isNil(temperature) ? temperature : ((temperature - 32) * 5) / 9
}

export function convertWeight(value: number, weightType: string) {
  return weightType === WeightTypes.KG
    ? round(lbsToKg(value), 2)
    : round(value, 4)
}

export function convertMetricWeightForDisplay(value: number) {
  if (value <= 10) {
    return round(value, 2)
  }

  return round(value, 1)
}

export function convertImperialWeightForDisplay(value: number) {
  if (value <= 25) {
    return round(value, 2)
  }

  return round(value, 1)
}

export function convertWeightForDisplay(value: number, weightType: string) {
  if (weightType === WeightTypes.KG) {
    return convertMetricWeightForDisplay(lbsToKg(value) as number)
  }

  return convertImperialWeightForDisplay(value)
}

export function convertTemperature(value: number, temperatureType: string) {
  return temperatureType === TemperatureTypes.F
    ? round(value)
    : round(fahrenheitToCelsius(value))
}

export function convertTemperatureForDisplay(
  value: number,
  temperatureType: string,
) {
  return convertTemperature(value, temperatureType)
}

export function convertUnits(
  field: UnitTypes | string,
  value: any,
  unitsState: UnitsState,
) {
  if (R.isNil(value)) {
    return undefined
  }

  if (field === UnitTypes.WEIGHT) {
    return convertWeight(value as number, unitsState[field])
  }

  if (field === UnitTypes.TEMPERATURE) {
    return convertTemperature(value as number, unitsState[field])
  }

  return value
}

export function convertUnitsForDisplay(
  field: UnitTypes | string,
  value: any,
  unitsState: UnitsState,
) {
  if (R.isNil(value)) {
    return undefined
  }

  if (field === UnitTypes.WEIGHT) {
    return convertWeightForDisplay(value as number, unitsState[field])
  }

  if (field === UnitTypes.TEMPERATURE) {
    return convertTemperatureForDisplay(value as number, unitsState[field])
  }

  return value
}

export const isKg = (unitsState: UnitsState) =>
  unitsState.weight === WeightTypes.KG
export const isLbs = (unitsState: UnitsState) =>
  unitsState.weight === WeightTypes.LBS

/**
 * Serialize our weight units to LBS - the initialValue is the value from BE
 */
export function serializeWeightUnit({
  field,
  unitsState,
  initialValue,
  fallback = null,
}: {
  fallback?: Primitive | Nil
  field: Field
  initialValue: any
  unitsState: UnitsState
}) {
  const hasValue = () => field.value > 0
  const touched = () => field.value !== field?.initialValue

  const serializeWeight: (field: Field) => any = R.cond([
    [() => isLbs(unitsState) && hasValue(), R.always(field.value)],
    [
      () => isKg(unitsState) && touched() && hasValue(),
      R.always(kgToLbs(Number(field.value))),
    ],
    [
      () => isKg(unitsState) && R.complement(touched) && hasValue(),
      R.always(initialValue),
    ],
    [R.T, R.always(fallback)],
  ])

  return serializeWeight(field)
}
